import React, { useState, useEffect, useRef } from 'react';
import { useConfirm } from '../context/ConfirmContext.tsx';
import { profileAPI, lookupAPI } from '../services/api.ts';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import MobileSelect from '../components/MobileSelect.tsx';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import SignatureCanvas from 'react-signature-canvas';

interface TransactionType {
  id: number;
  name: string;
}

interface TransactionRequest {
  id: number;
  transactionTypeId: number;
  transactionTypeName: string;
  bankDetails: string;
  amount: number;
  collectionMethod: string;
  collectionMethodId?: number;
  remarks: string;
  workFlow: string; // Backend returns "workFlow" with capital F
  requestDateTime: string;
  createdDate: string;
  status: string;
  workerSignatureUrl?: string | null; // Signature URL from backend
}

const TransactionRequestPage: React.FC = () => {
  const { confirm } = useConfirm();

  // Step 1: Form fields
  const [step, setStep] = useState<1 | 2>(1);
  const [transactionTypes, setTransactionTypes] = useState<TransactionType[]>([]);
  const [transactionTypeId, setTransactionTypeId] = useState<number>(0);
  const [amount, setAmount] = useState<string>('');
  const [remarks, setRemarks] = useState('');

  // Step 2: Signature
  const signaturePadRef = useRef<SignatureCanvas>(null);

  // Transaction history
  const [transactions, setTransactions] = useState<TransactionRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const pageSize = 10;
  const scrollRef = useRef<HTMLDivElement>(null);

  // Edit mode
  const [editingTransactionId, setEditingTransactionId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [viewingTransaction, setViewingTransaction] = useState<TransactionRequest | null>(null);
  const [signatureOnlyMode, setSignatureOnlyMode] = useState(false); // For re-signing existing transactions

  // Load transaction types and collection methods on mount
  useEffect(() => {
    loadTransactionTypes();
  }, []);

  // Reset pagination when switching to list view
  useEffect(() => {
    if (!showForm) {
      setPageNumber(1);
      setHasMore(true);
      setTransactions([]);
    }
  }, [showForm]);

  // Fetch transactions when pageNumber changes
  useEffect(() => {
    if (!showForm) {
      loadTransactions(pageNumber > 1);
    }
  }, [pageNumber, showForm]);

  // Set up scroll event listener for infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollContainer = scrollRef.current;
      if (scrollContainer && !loading && !isLoadingMore && hasMore && !showForm) {
        const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
        if (scrollTop + clientHeight >= scrollHeight - 100) {
          loadMoreTransactions();
        }
      }
    };
    const scrollContainer = scrollRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [loading, isLoadingMore, hasMore, showForm]);

  const loadTransactionTypes = async () => {
    try {
      const types = await lookupAPI.getTransactionTypes();
      setTransactionTypes(types);
      if (types.length > 0) {
        setTransactionTypeId(prev => prev || types[0].id);
      }
    } catch (error) {
      console.error('Error loading transaction types:', error);
      toast.error('Failed to load transaction types');
    }
  };

  const loadTransactions = async (isLoadingMore = false) => {
    if (!isLoadingMore) {
      setLoading(true);
    }
    try {
      const response = await profileAPI.getTransactionRequests({
        PageNumber: pageNumber,
        PageSize: pageSize
      });
      
      const newTransactions = response.data || [];
      
      if (isLoadingMore) {
        setTransactions(prev => [...prev, ...newTransactions]);
        setIsLoadingMore(false);
      } else {
        setTransactions(newTransactions);
      }
      
      // Check if there are more pages
      setHasMore(newTransactions.length === pageSize);
    } catch (error) {
      console.error('Error loading transactions:', error);
      toast.error('Failed to load transaction history');
      setHasMore(false);
    } finally {
      if (!isLoadingMore) {
        setLoading(false);
      }
    }
  };

  const loadMoreTransactions = () => {
    if (!hasMore || isLoadingMore) return;
    setIsLoadingMore(true);
    setPageNumber(prevPage => prevPage + 1);
  };

  const handleSubmitStep1 = (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!transactionTypeId) {
      toast.error('Please select a transaction type');
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    // Move to step 2 (signature)
    setStep(2);
  };

  const handleSubmitStep2 = async () => {
    try {
      // Validate signature is provided
      if (signaturePadRef.current && signaturePadRef.current.isEmpty()) {
        toast.error('Please provide a signature');
        return;
      }

      // Convert signature canvas to Blob/File
      let signatureFile: File | null = null;
      if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
        // Get the canvas as a blob
        const canvas = signaturePadRef.current.getCanvas();
        const blob = await new Promise<Blob | null>((resolve) => {
          canvas.toBlob(resolve, 'image/png');
        });
        
        if (blob) {
          // Convert blob to File
          signatureFile = new File([blob], 'signature.png', { type: 'image/png' });
        }
      }

      // Final validation: ensure we have a signature file
      if (!signatureFile) {
        toast.error('Please provide a signature');
        return;
      }

      // Special case: Signature-only mode
      if (signatureOnlyMode && editingTransactionId) {
        await profileAPI.updateSignature(editingTransactionId, signatureFile);
        toast.success('Signature updated successfully');
        
        // Reset form and refresh list
        resetForm();
        setShowForm(false);
        setPageNumber(1);
        setHasMore(true);
        setTransactions([]);
        return;
      }

      // Normal flow: Create or update full transaction
      // Create FormData for multipart upload
      const formData = new FormData();
      formData.append('TransactionTypeId', String(transactionTypeId));
      formData.append('Amount', String(parseFloat(amount)));
      formData.append('Remarks', remarks);
      formData.append('SignatureFile', signatureFile);

      if (editingTransactionId) {
        // Update existing transaction
        await profileAPI.updateTransactionRequest(editingTransactionId, formData);
        toast.success('Transaction request updated successfully');
      } else {
        // Create new transaction
        await profileAPI.submitTransactionRequest(formData);
        toast.success('Transaction request submitted successfully');
      }

      // Reset form and refresh list
      resetForm();
      setShowForm(false);
      setPageNumber(1);
      setHasMore(true);
      setTransactions([]);
    } catch (error: any) {
      console.error('Error submitting transaction request:', error);
      toast.error(error.message || 'Failed to submit transaction request');
    }
  };

  const handleClearSignature = () => {
    signaturePadRef.current?.clear();
  };

  const handleBackToStep1 = () => {
    setStep(1);
  };

  const resetForm = () => {
    setTransactionTypeId(transactionTypes[0]?.id || 0);
    setAmount('');
    setRemarks('');
    setStep(1);
    setEditingTransactionId(null);
    setSignatureOnlyMode(false);
    signaturePadRef.current?.clear();
  };

  const handleNewRequest = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEditTransaction = (transaction: TransactionRequest) => {
    console.log('Edit transaction:', transaction);
    
    // Check if this is signature-only mode (Pending or New workflow but no signature)
    const needsSignatureOnly = (transaction.workFlow === 'Pending' || transaction.workFlow === 'New') && 
                               (!transaction.workerSignatureUrl || transaction.workerSignatureUrl.trim() === '');
    
    if (needsSignatureOnly) {
      // Signature-only mode: skip to step 2
      setEditingTransactionId(transaction.id);
      setSignatureOnlyMode(true);
      setStep(2);
      setShowForm(true);
      console.log('Signature-only mode for transaction:', transaction.id);
    } else {
      // Normal edit mode: start from step 1
      setEditingTransactionId(transaction.id);
      setSignatureOnlyMode(false);
      setTransactionTypeId(transaction.transactionTypeId);
      setAmount(transaction.amount.toString());
      setRemarks(transaction.remarks || '');
      setStep(1);
      setShowForm(true);
      
      console.log('Set values:', {
        transactionTypeId: transaction.transactionTypeId,
        amount: transaction.amount.toString(),
        remarks: transaction.remarks
      });
    }
  };

  const handleDeleteTransaction = async (id: number) => {
    const confirmed = await confirm('Are you sure you want to delete this transaction request?');
    if (confirmed) {
      try {
        await profileAPI.deleteTransactionRequest(id);
        toast.success('Transaction request deleted successfully');
        // Refresh the list
        setPageNumber(1);
        setHasMore(true);
        setTransactions([]);
      } catch (error: any) {
        console.error('Error deleting transaction:', error);
        toast.error(error.message || 'Failed to delete transaction request');
      }
    }
  };

  const handleViewTransaction = (transaction: TransactionRequest) => {
    setViewingTransaction(transaction);
  };

  const handleCloseView = () => {
    setViewingTransaction(null);
  };

  // Quill modules configuration
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      ['clean']
    ]
  };

  return (
    <div className="container-app py-4 md:py-6">
      <ToastContainer position="top-right" autoClose={3000} />
      
      {/* View Transaction Modal */}
      {viewingTransaction && (
        <div className="fixed inset-0 z-[100] flex items-end md:items-center justify-center bg-black/50" onClick={handleCloseView}>
          <div 
            className="bg-white w-full md:max-w-2xl md:rounded-lg rounded-t-2xl max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Transaction Details</h3>
              <button onClick={handleCloseView} className="text-gray-500 hover:text-gray-700">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
              {/* Status Badge */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Status</span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  viewingTransaction.workFlow === 'Approved'
                    ? 'bg-green-100 text-green-800'
                    : viewingTransaction.workFlow === 'Rejected'
                    ? 'bg-red-100 text-red-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {viewingTransaction.workFlow || 'Pending'}
                </span>
              </div>

              {/* Transaction Type */}
              <div>
                <div className="text-sm text-gray-500 mb-1">Transaction Type</div>
                <div className="text-base font-medium text-gray-900">{viewingTransaction.transactionTypeName}</div>
              </div>

              {/* Request Date/Time */}
              <div>
                <div className="text-sm text-gray-500 mb-1">Requested On</div>
                <div className="text-base text-gray-900">
                  {new Date(viewingTransaction.requestDateTime || viewingTransaction.createdDate).toLocaleDateString(undefined, { 
                    day: '2-digit', 
                    month: 'short', 
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>

              {/* Amount */}
              <div>
                <div className="text-sm text-gray-500 mb-1">Amount</div>
                <div className="text-3xl font-bold text-gray-900">${viewingTransaction.amount.toFixed(2)}</div>
              </div>

              {/* Collection Method */}
              <div>
                <div className="text-sm text-gray-500 mb-1">Collection Method</div>
                <div className="text-base text-gray-900">{viewingTransaction.collectionMethod}</div>
              </div>

              {/* Bank Details */}
              <div>
                <div className="text-sm text-gray-500 mb-1">Bank Details</div>
                <div className="text-base text-gray-900">{viewingTransaction.bankDetails}</div>
              </div>

              {/* Remarks */}
              {viewingTransaction.remarks && viewingTransaction.remarks.trim() && (
                <div>
                  <div className="text-sm text-gray-500 mb-1">Remarks</div>
                  <div 
                    className="text-base text-gray-700 border border-gray-200 rounded p-3 bg-gray-50"
                    dangerouslySetInnerHTML={{ __html: viewingTransaction.remarks }}
                  />
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div className="sticky bottom-0 bg-white border-t px-4 py-3 flex gap-2">
              {(viewingTransaction.workFlow === 'Pending' || viewingTransaction.workFlow === 'New') && (
                <button
                  onClick={() => {
                    handleEditTransaction(viewingTransaction);
                    handleCloseView();
                  }}
                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors font-medium"
                >
                  Edit Transaction
                </button>
              )}
              <button
                onClick={handleCloseView}
                className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto">
        {/* Header with single button like Leave page */}
        <section className="mb-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Transaction</h2>
            <button
              onClick={() => {
                if (editingTransactionId) {
                  resetForm();
                  setShowForm(false);
                } else {
                  setShowForm(v => !v);
                }
              }}
              className="bg-primary text-white px-3 py-2 rounded"
            >
              {editingTransactionId ? 'Cancel Edit' : showForm ? 'Close' : 'New Request'}
            </button>
          </div>
        </section>

        {showForm ? (
          <div key={editingTransactionId || 'new'} className="bg-white rounded-lg shadow-md p-4 md:p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              {signatureOnlyMode 
                ? 'Add Signature' 
                : editingTransactionId 
                  ? 'Edit Transaction Request' 
                  : 'New Transaction Request'}
            </h2>

            {/* Step indicator - hide in signature-only mode */}
            {!signatureOnlyMode && (
              <div className="flex items-center justify-center mb-6">
                <div className="flex items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                      step === 1 ? 'bg-[#D92D30] text-white' : 'bg-green-500 text-white'
                    }`}
                  >
                    1
                  </div>
                  <div className={`w-16 h-1 ${step === 2 ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                      step === 2 ? 'bg-[#D92D30] text-white' : 'bg-gray-300 text-gray-600'
                    }`}
                  >
                    2
                  </div>
                </div>
              </div>
            )}

            {/* Signature-only mode: Show message */}
            {signatureOnlyMode && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-yellow-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-yellow-800">Signature Required</p>
                    <p className="text-sm text-yellow-700 mt-1">
                      This transaction is missing your signature. Please sign below to complete your request.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {step === 1 && !signatureOnlyMode ? (
              <form onSubmit={handleSubmitStep1}>
                {/* Transaction Type */}
                <div className="mb-4">
                  <MobileSelect
                    label="Transaction Type *"
                    value={transactionTypeId ? String(transactionTypeId) : ''}
                    options={transactionTypes.map(type => ({
                      value: String(type.id),
                      label: type.name
                    }))}
                    onChange={(val) => setTransactionTypeId(Number(val))}
                    placeholder="Select Transaction Type"
                  />
                </div>

                {/* Amount */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#D92D30]"
                    required
                  />
                </div>

                {/* Remarks (Rich Text) */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Remarks
                  </label>
                  <ReactQuill
                    theme="snow"
                    value={remarks}
                    onChange={setRemarks}
                    modules={quillModules}
                    className="bg-white"
                    placeholder="Enter any additional remarks..."
                  />
                </div>

                <div className="flex gap-3">
                  {editingTransactionId && (
                    <button
                      type="button"
                      onClick={() => {
                        resetForm();
                        setShowForm(false);
                      }}
                      className="flex-1 bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition-colors"
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    type="submit"
                    className="flex-1 bg-[#D92D30] text-white px-4 py-2 rounded-md hover:bg-[#D92D30] transition-colors"
                  >
                    Next: Signature →
                  </button>
                </div>
              </form>
            ) : (
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-4">
                  {signatureOnlyMode ? 'Signature' : 'Step 2: Signature'}
                </h3>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Please sign below <span className="text-red-500">*</span>
                  </label>
                  <div className="border-2 border-gray-300 rounded-md">
                    <SignatureCanvas
                      ref={signaturePadRef}
                      canvasProps={{
                        className: 'w-full h-48 bg-white rounded-md',
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleClearSignature}
                    className="mt-2 text-sm text-[#D92D30] hover:underline"
                  >
                    Clear Signature
                  </button>
                </div>

                <div className="flex gap-3">
                  {!signatureOnlyMode && (
                    <button
                      type="button"
                      onClick={handleBackToStep1}
                      className="flex-1 bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition-colors"
                    >
                      ← Back
                    </button>
                  )}
                  {signatureOnlyMode && (
                    <button
                      type="button"
                      onClick={() => {
                        resetForm();
                        setShowForm(false);
                      }}
                      className="flex-1 bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition-colors"
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleSubmitStep2}
                    className="flex-1 bg-[#D92D30] text-white px-4 py-2 rounded-md hover:bg-[#D92D30] transition-colors"
                  >
                    {signatureOnlyMode ? 'Submit Signature' : 'Submit Request'}
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col" style={{ height: 'calc(100vh - 200px)' }}>
       
            <div ref={scrollRef} className="flex-1 overflow-y-auto">
              {loading && transactions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">Loading...</div>
              ) : transactions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No transaction requests found. Click "New Request" to create one.
                </div>
              ) : (
                <>
                  {/* Mobile-friendly card layout */}
                  <div className="divide-y">
                    {transactions.map((transaction) => {
                      const canEdit = transaction.workFlow === 'Pending' || transaction.workFlow === 'New';
                      const needsSignature = canEdit && (!transaction.workerSignatureUrl || transaction.workerSignatureUrl.trim() === '');
                      const statusClass = 
                        transaction.workFlow === 'Approved'
                          ? 'bg-green-100 text-green-800'
                          : transaction.workFlow === 'Rejected'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800';
                      
                      // Format date
                      const formatDate = (dateStr: string) => {
                        try {
                          const date = new Date(dateStr);
                          return date.toLocaleDateString(undefined, { 
                            day: '2-digit', 
                            month: 'short', 
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          });
                        } catch {
                          return dateStr;
                        }
                      };

                      return (
                        <div key={transaction.id} className="bg-white p-4 hover:bg-gray-50">
                          {/* Header: Type and Status */}
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <h3 className="font-semibold text-gray-900 text-base">
                                {transaction.transactionTypeName}
                              </h3>
                              <div className="text-xs text-gray-500 mt-1">
                                {formatDate(transaction.requestDateTime || transaction.createdDate)}
                              </div>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusClass}`}>
                              {transaction.workFlow || 'Pending'}
                            </span>
                          </div>

                          {/* Amount - Large and prominent */}
                          <div className="mb-3">
                            <div className="text-2xl font-bold text-gray-900">
                              RM {transaction.amount.toFixed(2)}
                            </div>
                          </div>

                          {/* Details Grid */}
                          <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
                            <div>
                              <div className="text-xs text-gray-500 uppercase">Collection</div>
                              <div className="font-medium text-gray-900">{transaction.collectionMethod}</div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 uppercase">Bank Details</div>
                              <div className="font-medium text-gray-900 truncate">{transaction.bankDetails}</div>
                            </div>
                          </div>

                          {/* Remarks (if available) */}
                          {transaction.remarks && transaction.remarks.trim() && (
                            <div className="mb-3 text-sm">
                              <div className="text-xs text-gray-500 uppercase mb-1">Remarks</div>
                              <div 
                                className="text-gray-700 line-clamp-2"
                                dangerouslySetInnerHTML={{ __html: transaction.remarks }}
                              />
                            </div>
                          )}

                          {/* Signature warning */}
                          {needsSignature && (
                            <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded-md flex items-center gap-2">
                              <svg className="w-4 h-4 text-yellow-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                              </svg>
                              <span className="text-xs text-yellow-800 font-medium">Signature Required</span>
                            </div>
                          )}

                          {/* Action buttons */}
                          <div className="flex gap-2 pt-3 border-t border-gray-100">
                            <button
                              onClick={() => handleViewTransaction(transaction)}
                              className="flex-1 px-3 py-2 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors font-medium text-sm flex items-center justify-center gap-1"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              View
                            </button>
                            {canEdit && (
                              <>
                                <button
                                  onClick={() => handleEditTransaction(transaction)}
                                  className={`flex-1 px-3 py-2 rounded-md transition-colors font-medium text-sm flex items-center justify-center gap-1 ${
                                    needsSignature 
                                      ? 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100'
                                      : 'bg-green-50 text-green-700 hover:bg-green-100'
                                  }`}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    {needsSignature ? (
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                    ) : (
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    )}
                                  </svg>
                                  {needsSignature ? 'Sign' : 'Edit'}
                                </button>
                                <button
                                  onClick={() => handleDeleteTransaction(transaction.id)}
                                  className="flex-1 px-3 py-2 bg-red-50 text-red-700 rounded-md hover:bg-red-100 transition-colors font-medium text-sm flex items-center justify-center gap-1"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                  Delete
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                {/* Loading more indicator */}
                {isLoadingMore && (
                  <div className="text-center py-4 text-gray-500">
                    Loading more...
                  </div>
                )}

                {/* End of list indicator */}
                {!hasMore && transactions.length > 0 && (
                  <div className="text-center py-4 text-gray-400 text-sm">
                    No more transactions
                  </div>
                )}
              </>
            )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionRequestPage;

